/**
 * Lectura del payload de un JWT y chequeo de vencimiento del lado del front.
 *
 * NO valida la firma: solo sirve para decidir si vale la pena mandar el token
 * (el backend sigue siendo la autoridad). Se usa para no publicar una sesión
 * restaurada con el token ya vencido y para no disparar requests que van a
 * volver 401.
 */

/** Payload del JWT como objeto, o `null` si no es un JWT decodificable. */
export function decodeJwtPayload(token: string | null | undefined): { [claim: string]: any } | null {
  try {
    if (typeof token !== 'string') return null;

    const parts = token.split('.');
    if (parts.length !== 3 || !parts[1]) return null;

    let b64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    while (b64.length % 4) b64 += '=';
    const payload = JSON.parse(atob(b64));

    return !!payload && typeof payload === 'object' ? payload : null;
  } catch (e) {
    return null;
  }
}

/**
 * `token_data.expires` de api2 viene como `gmdate('Y-m-d H:i:s (e)')`, p.ej.
 * `2026-10-01 12:00:00 (UTC)`, que `Date.parse` no entiende en todos los
 * navegadores. Se lee como UTC. Cualquier otro formato se intenta con
 * `Date.parse`. Devuelve epoch en ms, o `null` si no se puede leer.
 */
function parseExpires(expires: any): number | null {
  if (typeof expires !== 'string' || !expires.trim()) return null;

  const m = /^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})(?:\s*\((?:UTC|GMT|Z)\))?$/.exec(expires.trim());
  if (m) {
    return Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +m[6]);
  }

  const parsed = Date.parse(expires);
  return Number.isFinite(parsed) ? parsed : null;
}

/**
 * `true` solo si se puede afirmar que el token está vencido:
 * - JWT con `exp` numérico y `exp < ahora - skewSec`, o
 * - si no es un JWT con `exp` legible, `token_data.expires` (cuando viene) ya pasó
 *   por más de `skewSec`.
 *
 * Si no se puede determinar devuelve `false`, para no romper sesiones legacy
 * sin vencimiento legible. El margen tolera relojes del cliente adelantados.
 */
export function isTokenExpired(
  token: { access_token?: string; expires?: string } | string | null | undefined,
  skewSec: number = 30
): boolean {
  if (!token) return false;

  const accessToken = typeof token === 'string' ? token : token.access_token;
  const nowMs = Date.now();
  const skewMs = skewSec * 1000;

  const payload = decodeJwtPayload(accessToken);
  if (payload && typeof payload.exp === 'number' && Number.isFinite(payload.exp)) {
    return payload.exp * 1000 < nowMs - skewMs;
  }

  const expiresMs = typeof token === 'string' ? null : parseExpires(token.expires);
  if (expiresMs !== null) {
    return expiresMs < nowMs - skewMs;
  }

  return false;
}
